<template>
<section class="order-submission-payment">
  <section v-if="!hideBonusesSection" class="order-submission-payment__section order-submission-payment__bonuses-section form-card">
    <h4 class="order-submission-payment__section-header">
      <template v-if="!shouldDisplayBonusesOnDemand || bonusesBalanceLoaded">
        Баланс бонусов: {{ bonusesBalance }}
      </template>
      <template v-else>
        Оплата бонусами
      </template>
    </h4>
    <template v-if="shouldDisplayBonusesOnDemand && !bonusesBalanceLoaded">
      <p class="text-sm">
        <a v-if="!bonusesBalanceIsResolving"
           href="javascript:;"
           class="text-primary hover:text-primary-dark border-b border-dashed border-primary"
           @click="resolveBonusesAndMaxPayment"
        >
          Показать баланс бонусов
        </a>
        <template v-else>Пожалуйста, подождите...</template>
      </p>
      <p v-if="resolvedBonusesFailed" class="text-sm text-danger mt-4">
        Не удалось получить баланс бонусов. Пожалуйста, попробуйте еще раз.
      </p>
    </template>

    <template v-if="shouldDisplayBonusesInput">
      <CustomInput label=""
                   :value="bonusesPaymentAmount"
                   @input="onBonusesPaymentInput"
                   type="number"
                   inputmode="numeric"
                   :max="maxBonusesPayment ? maxBonusesPayment.value / 100 : undefined"
                   min="0"
                   step="0.01"
                   autocomplete="off"
                   class="order-submission-payment__bonuses-amount-input"
                   placeholder="Списать бонусы"
      />
      <span class="order-submission-payment__max-payment-text">
        Можно оплатить до
        <button @click="setMaxBonusesPayment" class="order-submission-payment__max-payment-text-amount">
          {{ maxBonusesPayment ? `${maxBonusesPayment.short}${maxBonusesPayment.currency.symbol}` : '...' }}</button>
      </span>
    </template>
  </section>
  <section class="order-submission-payment__section order-submission-payment__real-money-section form-card">
    <h4 class="order-submission-payment__section-header">Оплата</h4>
    <div class="form-radio-group form-radio-group--inline order-submission-payment-options">
      <label
        v-for="orderPaymentMethod in availablePaymentMethods"
        :key="orderPaymentMethod.key"
        class="custom-radio"
      >
        <span class="custom-radio-label">{{ orderPaymentMethod.name }}</span>
        <input
          type="radio"
          :checked="currentPaymentMethod === orderPaymentMethod.key"
          @change="choosePaymentMethod(orderPaymentMethod)"
        />
        <span class="radio-button"></span>
      </label>
    </div>
    <TransitionExpand>
      <div v-if="payment_method === 'cash'" class="bg-primary-lightest p-4 mt-2 rounded">
        <label class="order-submission-payment__bills-section-label form-control-label mt-0">Купюра к оплате</label>
        <div class="order-submission-payment__input-and-suggestions">
          <!-- type="button", because pressing enter on the bills input was causing the first button to be pressed. -->
          <button
            @click="() => {
              $emit('update:expected_payment_amount', toPayWithRealMoneyAmount);
              showBillsInput = false;
            }"
            class="order-submission-payment__bills-suggestion"
            :class="{
              'order-submission-payment__bills-suggestion--active':
                expected_payment_amount === toPayWithRealMoneyAmount && !showBillsInput
            }"
            type="button"
          >Без сдачи</button>
          <button
            v-for="s of billsSuggestions"
            @click="() => { $emit('update:expected_payment_amount', s); showBillsInput = false; }"
            :key="s"
            class="order-submission-payment__bills-suggestion"
            :class="{
              'order-submission-payment__bills-suggestion--active': expected_payment_amount === s && !showBillsInput
            }"
            type="button"
          >{{ currencyPrefix }}{{ s }}{{ currencySuffix }}</button>
          <button
            @click="() => {
              showBillsInput = true;
              $nextTick(() => {
                const input = $refs.billsInput.$el.querySelector('input')
                input.focus();
                input.select();
              });
            }"
            class="order-submission-payment__bills-suggestion"
            :class="{ 'order-submission-payment__bills-suggestion--active': showBillsInput }"
            type="button"
          >Другая...</button>
          <CustomInput
            v-if="showBillsInput"
            ref="billsInput"
            label=""
            :value="expected_payment_amount"
            @input="$event => {
              const parsed = parseFloat($event, 10);
              if (Number.isNaN(parsed)) return;
              $emit('update:expected_payment_amount', parsed)
            }"
            @change="$event => {
              const parsed = parseFloat($event.target.value, 10);
              if (Number.isNaN(parsed)) {
                $emit('update:expected_payment_amount', toPayWithRealMoneyAmount !== null
                  ? toPayWithRealMoneyAmount
                  : null
                );
              }
            }"
            type="number"
            inputmode="numeric"
            :min="toPayWithRealMoneyAmount ? toPayWithRealMoneyAmount : undefined"
            step="0.01"
            autocomplete="off"
            class="order-submission-payment__bills-input"
          />
        </div>
      </div>
      <template v-else-if="payment_method === 'cloudpayments'">
        <div class="cloudpayments-payment-wrapper">
          <div class="bg-primary-lightest p-4 mt-2 rounded">
            <label class="custom-checkbox">
              <span class="custom-checkbox-label text-sm">
                Сохранить карту для быстрой оплаты в будущем
              </span>
              <input type="checkbox" :value="true" v-model="onlinePayment.saveCard" @change="$emit('update:saveCard', onlinePayment.saveCard)" />
              <span class="checkbox"></span>
            </label>
            <p class="text-xs mt-2">Мы храним только цифровой отпечаток вашей карты. Полный номер карты не сохраняется.</p>
          </div>
        </div>
      </template>
      <div v-else-if="isUsingCardTokenPaymentMethod">
        <div class="card-token-payment-wrapper">
          <div class="bg-primary-lightest p-4 mt-2 rounded">
            <p class="text-sm">Оплата будет выполнена с ранее сохранённой карты.</p>
            <p class="text-sm mt-2">
              Вы можете отвязать карту в любой момент в <router-link :to="{ name: 'dashboard' }" class="underline">профиле</router-link>.
            </p>
          </div>
        </div>
      </div>
    </TransitionExpand>
  </section>
</section>
</template>

<script>
import axios from '@/zenky';
import { mapGetters, mapActions } from 'vuex';
import TransitionExpand from '@/components/AsyncTransitionExpand';
import CustomInput from '@/components/inputs/CustomInput';
import getBillsSuggestions from '@/helpers/getBillsSuggestions';
import HasCurrencyData from '@/mixins/HasCurrencyData'

export default {
  components: { CustomInput, TransitionExpand },

  mixins: [HasCurrencyData],

  props: {
    payment_method: {
      type: String,
      required: true,
    },
    expected_payment_amount: {
      required: true,
      validator(v) {
        return v === null || Number.isFinite(v);
      },
    },
    card_token_id: {
      required: false,
      type: String,
      default: '',
    },
    toPayWithRealMoneyAmount: {
      required: true,
      validator(v) {
        return v === null || Number.isFinite(v);
      }
    },
    maxBonusesPayment: {
      type: Object,
      required: false,
    },
    bonusesPaymentAmount: {
      type: Number,
      required: true,
    },
    // bonusesPaymentConfirmed: {
    //   type: Boolean,
    //   required: true,
    // },
    hideBonusesSection: {
      type: Boolean,
      required: true,
    },
  },

  mounted() {
    this.loadOrderPaymentMethods();
  },

  computed: {
    ...mapGetters({
      storePaymentMethods: 'store/orderPaymentMethods',
      shouldDisplayBonusesOnDemand: 'store/shouldDisplayBonusesOnDemand',
      loyaltyProfile: 'auth/loyaltyProfile',
      paymentsPreviewIsLoading: 'order/paymentsPreviewIsLoading',
    }),

    noBillsSuggestionsMatchTheValue() {
      if (this.expected_payment_amount === null) return false
      return this.billsSuggestions.find(s => s === this.expected_payment_amount) === undefined
        && (this.toPayWithRealMoneyAmount !== null && this.expected_payment_amount !== this.toPayWithRealMoneyAmount);
    },

    billsSuggestions() {
      return getBillsSuggestions(this.toPayWithRealMoneyAmount).filter(s => s !== this.toPayWithRealMoneyAmount);
    },

    availablePaymentMethods() {
      const methods = Array.isArray(this.orderPaymentMethods) && this.orderPaymentMethods.length > 0
        ? this.orderPaymentMethods
        : this.storePaymentMethods;

      return methods.map(method => {
        if (method.id === 'card_token') {
          method.key = `card_token_${method.token.id}`;
        } else {
          method.key = method.id;
        }

        return method;
      });
    },

    isUsingCardTokenPaymentMethod() {
      return this.payment_method === 'card_token';
    },

    bonusesBalance() {
      if (this.shouldDisplayBonusesOnDemand) {
        return this.bonusesBalanceLoaded ? this.resolvedBonuses.full : null;
      } else if (!this.loyaltyProfile) {
        return null;
      }

      return this.loyaltyProfile.balance.full;
    },

    bonusesBalanceLoaded() {
      return this.resolvedBonuses !== null;
    },

    bonusesBalanceIsResolving() {
      return this.paymentsPreviewIsLoading || this.resolvedBonusesAreLoading;
    },

    shouldDisplayBonusesInput() {
      if (!this.shouldDisplayBonusesOnDemand) {
        return this.maxBonusesPayment && this.maxBonusesPayment.value > 0;
      } else if (!this.bonusesBalanceLoaded) {
        return false;
      }

      return this.maxBonusesPayment && this.maxBonusesPayment.value > 0;
    },
  },

  methods: {
    ...mapActions({
      loadPaymentMethods: 'order/loadPaymentMethods',
      loadPaymentsPreview: 'order/loadPaymentsPreview',
    }),

    async loadOrderPaymentMethods() {
      try {
        this.orderPaymentMethods = await this.loadPaymentMethods();
      } catch (e) {
        this.orderPaymentMethods = [];
      }
    },

    choosePaymentMethod(method) {
      this.currentPaymentMethod = method.key;

      this.$emit('update:payment_method', method.id);

      if (method.id === 'card_token') {
        this.$emit('update:card_token_id', method.token.id);
      } else {
        this.$emit('update:card_token_id', '');
      }
    },

    onBonusesPaymentInput(e) {
      const parsed = parseFloat(e);

      if (Number.isNaN(parsed)) {
        this.$emit('update:bonuses-payment-amount', 0)
        return;
      }

      this.$emit('update:bonuses-payment-amount', parsed)
    },

    setMaxBonusesPayment() {
      if (this.maxBonusesPayment) {
        this.$emit('update:bonuses-payment-amount', this.maxBonusesPayment.value / 100)
      }
    },

    async resolveBonusesAndMaxPayment() {
      await this.resolveBonusesBalance();
      await this.loadPaymentsPreview();
    },

    async resolveBonusesBalance() {
      if (this.resolvedBonusesAreLoading) {
        return;
      }

      this.resolvedBonusesAreLoading = true;
      this.resolvedBonusesFailed = false;

      try {
        this.resolvedBonuses = (await axios.get('profile/loyalty/balance')).data.data.balance;

        if (this.resolvedBonuses === null) {
          this.resolvedBonusesFailed = true;
        }
      } catch (e) {
        this.resolvedBonuses = null;
        this.resolvedBonusesFailed = true;
      } finally {
        this.resolvedBonusesAreLoading = false;
      }
    },
  },

  watch: {
    maxBonusesPayment(newVal, oldVal) {
      if (!!oldVal && !!newVal && this.bonusesPaymentAmount > 0 && this.bonusesPaymentAmount.toFixed(2) === (oldVal.value / 100).toFixed(2)) {
        this.$emit('update:bonuses-payment-amount', newVal.value / 100);
      }
    },
    toPayWithRealMoneyAmount: {
      immediate: true,
      handler(newVal, oldVal) {
        // Similar to `maxBonusesPayment` watcher.
        if (!newVal) {
          return;
        }

        if (!!newVal && this.expected_payment_amount === null) {
          this.$emit('update:expected_payment_amount', newVal);
          return;
        }

        if (oldVal === this.expected_payment_amount) {
          this.$emit('update:expected_payment_amount', newVal);
        }
      },
    },
    noBillsSuggestionsMatchTheValue: {
      immediate: true,
      handler(newVal) {
        if (newVal === true) {
          setTimeout(() => { // In case this only lasts for one event cycle.
            if (this.noBillsSuggestionsMatchTheValue) {
              this.showBillsInput = true;
            }
          });
        }
      },
    },
  },

  data() {
    return {
      showBillsInput: false,
      onlinePayment: {
        saveCard: false,
      },
      orderPaymentMethods: [],
      currentPaymentMethod: 'credit-card',
      resolvedBonuses: null,
      resolvedBonusesAreLoading: false,
      resolvedBonusesFailed: false,
    };
  },
}
</script>
